import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "@mui/icons-material";
import { Alert, Box, Divider, FormHelperText, Link as MuiLink, Stack, Typography } from "@mui/joy";
import { ApiCompetitionDetail } from "griptonite-api-client";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";

import queryClient from "@/core/queryClient";
import { useCompetitionQuery, useUpdateCompetitionMutation } from "@/hooks/competitions";
import { useCurrentVenueQuery } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import CheckboxInput from "../forms/CheckboxInput";
import { NumberInput, TextareaInput } from "../forms/TextInput";
import { ToggleButtonSelect, ToggleButtonSelectInput } from "../forms/ToggleButtonSelect";
import { Link } from "../Links";
import BottomButtons from "./BottomButtons";
import CustomScoringTableInput from "./CustomScoringTableInput";
import {
  competitionToScoringFormValues,
  ScoringFormSchema,
  ScoringFormValues,
  scoringFormValuesToPayload,
} from "./forms";
import { useUnhidePreviousTabWarningsOnMount } from "./getPublishWarnings";
import PublishWarningsAlert from "./PublishWarningsAlert";

const CompetitionScoringForm = ({ competitionUuid }: { competitionUuid: string }) => {
  const { data: venue } = useCurrentVenueQuery();

  const {
    data: competitionSummaryOrDetail,
    isFetching,
    isPending,
  } = useCompetitionQuery(competitionUuid);

  const detailPending = isPending || (!competitionSummaryOrDetail?.settings && isFetching);

  const competition = competitionSummaryOrDetail as ApiCompetitionDetail;

  const defaultValues = useMemo(() => competitionToScoringFormValues(competition), [competition]);

  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(ScoringFormSchema),
    defaultValues,
  });

  useUnhidePreviousTabWarningsOnMount(competitionUuid, "details", "categories", "circuits");

  useEffect(() => {
    methods.reset(defaultValues as ScoringFormValues);
  }, [defaultValues, methods]);

  const updateMutation = useUpdateCompetitionMutation(
    competitionUuid,
    venue?.uuid,
    competition?.league?.uuid,
  );

  if (detailPending) {
    return null;
  }

  const onSubmitBase = async (values: ScoringFormValues, isPublish?: boolean) => {
    if (!venue) {
      return;
    }
    const payload = scoringFormValuesToPayload(values);

    await updateMutation.mutateAsync(
      {
        ...payload,
        ...(isPublish ? { isHidden: false } : {}),
      },
      {
        onSuccess: () => {
          // Route properties (e.g. segments count) may have changed, so as well as the default invalidations
          // we should also invalidate routes
          queryClient.invalidateQueries({
            queryKey: ["venues", venue.uuid, "routes"],
          });
        },
        onError: async (error) => {
          handleFormError(error, methods);
        },
      },
    );
  };

  const onSubmit = (values: ScoringFormValues) => onSubmitBase(values, false);
  const onPublish = (values: ScoringFormValues) => onSubmitBase(values, true);

  return (
    <Stack
      sx={{ padding: 1.5, gap: 1.5, flex: 1, overflowY: "auto" }}
      component="form"
      autoComplete="off"
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <FormProvider {...methods}>
        <PublishWarningsAlert tab="scoring" competition={competition} />
        <Stack flex={1} spacing={1}>
          <FormErrorAlert />
          <FormFields competition={competition} />
        </Stack>
        <BottomButtons
          formState={methods.formState}
          prevLink={{
            to: "/gym/$venueUuid/events/$competitionUuid/profile/circuits",
            replace: true,
          }}
          competition={competition}
          isFinalStep
          onPublishClick={() => {
            methods.handleSubmit(onPublish)();
          }}
        />
      </FormProvider>
    </Stack>
  );
};

const FormFields = ({ competition }: { competition: ApiCompetitionDetail }) => {
  const boulderSystemOptions = [
    { value: "ATTEMPTS", name: "Attempts" },
    { value: "TICKS_ONLY", name: "Ticks" },
    { value: "DYNAMIC", name: "Dynamic" },
    { value: "CIRCUIT_NUMBER", name: "Redpoint" },
    { value: "IFSC_RANKING", name: "Ranking" },
    { value: "IFSC_2025", name: "World Cup" },
    { value: "OLYMPICS_2024", name: "Olympics" },
    { value: "CUSTOM", name: "Custom" },
  ];
  const sportSystemOptions = [{ value: "IFSC_SPORT", name: "IFSC Sport" }];
  const helpTextBySystem = {
    ATTEMPTS: "Points depend on the number of tries - fewer tries mean more points.",
    TICKS_ONLY: "Points are awarded for any tick of the route, no matter how many tries needed.",
    DYNAMIC: (
      <>
        Each problem has points shared out between all climbers who tick it. <br />
        For example, 1 person ticks they get all the points; 10 people tick they each get 1/10th the
        points.
      </>
    ),
    CIRCUIT_NUMBER: (
      <>
        In this scoring system, the score for topping a route is its number multiplied by the points
        multiplier. <br />
        For example, if the points multiplier is 100 and the competitors tops route 5, they get 500
        points. <br />
        <MuiLink
          href="https://griptonite.io/support/redpoint-scoring"
          sx={{ fontSize: 14 }}
          target="_blank"
        >
          Check out our FAQ for more details
        </MuiLink>
      </>
    ),
    IFSC_RANKING:
      "2024 world cup scoring. Each problem has a zone hold. Competitors are ranked by tops, zones, tries to top, and tries to zone.",
    IFSC_SPORT: (
      <>
        Scores are calculated according to the IFSC rules. See{" "}
        <MuiLink
          href="https://www.ifsc-climbing.org/index.php/world-competition"
          target="_blank"
          sx={{ fontSize: 12 }}
        >
          the IFSC website
        </MuiLink>{" "}
        for more information.
      </>
    ),
    IFSC_2025:
      "2025 world cup scoring. Each problem has a zone hold. Competitors receive 25 points for a top or 10 points for zoning a route. There is a penalty of 0.1 points per failed attempt.",
    OLYMPICS_2024: (
      <>
        Each problem has two zone holds. Competitors receive:
        <ul>
          <li>25 points for a top</li>
          <li>10 points for reaching zone 2</li>
          <li>5 points for reaching zone 1</li>
        </ul>
        There is a penalty of 0.1 points per failed attempt.
      </>
    ),
    CUSTOM: "Manually specify custom point allocations",
  } as const;

  const scoringSystem:
    | "ATTEMPTS"
    | "TICKS_ONLY"
    | "DYNAMIC"
    | "CIRCUIT_NUMBER"
    | "IFSC_RANKING"
    | "IFSC_SPORT" = useWatch({ name: "scoringSystem" });

  const subComponent = {
    ATTEMPTS: <AttemptsFormFields />,
    TICKS_ONLY: <TicksOnlyFormFields />,
    DYNAMIC: <DynamicFormFields />,
    CIRCUIT_NUMBER: <CircuitNumberFormFields />,
    IFSC_RANKING: <IFSCRankingFormFields />,
    IFSC_SPORT: <IFSCSportFormFields />,
    CUSTOM: <CustomFormFields />,
  };

  return (
    <>
      <Box sx={{ label: { fontSize: "1.1rem!important" } }}>
        <ToggleButtonSelectInput
          name="scoringSystem"
          label="Scoring system"
          options={competition?.climbType === "SPORT" ? sportSystemOptions : boulderSystemOptions}
          nullable={false}
        />
        <FormHelperText>
          <Box>{helpTextBySystem[scoringSystem]}</Box>
        </FormHelperText>
      </Box>
      {subComponent[scoringSystem]}
      <Box sx={{ paddingY: 1 }}>
        <Divider sx={{ marginX: -1.5 }} />
      </Box>
      <Box>
        <Typography level="title-lg" marginBottom={1.5}>
          Other settings
        </Typography>
        <CheckboxInput name="liveLeaderboard" label="Enable live leaderboard" />
        <FormHelperText>
          If enabled, users will be able to see the leaderboard in real time in the app and on the
          TV. <br />
          Otherwise, it will be hidden until the competition ends.
        </FormHelperText>
      </Box>
    </>
  );
};

const CustomFormFields = () => {
  const segmentsCount = useWatch({
    name: "customSettings.segmentsCount",
  });

  const maxRoutes = useWatch({ name: "customSettings.maxRoutes" });
  const enableFixedPenalty = useWatch({
    name: "customSettings.useFailedAttemptPenalty",
  });
  const { setValue } = useFormContext();
  const [isAll, setIsAll] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (isAll) {
      setValue("customSettings.maxRoutes", null);
    }
  }, [isAll, setValue]);

  useEffect(() => {
    if (isAll === undefined) {
      setIsAll(!maxRoutes);
    }
  }, [isAll, maxRoutes]);

  useEffect(() => {
    if (!enableFixedPenalty) {
      setValue("customSettings.failedAttemptPenalty", null);
      setValue("customSettings.flashBonus", null);
    }
  }, [enableFixedPenalty, setValue]);

  return (
    <>
      <Box sx={{ paddingY: 1 }}>
        <Divider sx={{ marginX: -1.5 }} />
      </Box>
      <Typography level="title-lg">System options</Typography>
      <ToggleButtonSelectInput
        name="customSettings.segmentsCount"
        label="Zone holds"
        options={[
          { value: 0, name: "No zones" },
          {
            value: 1,
            name: "Single zone",
          },
          {
            value: 2,
            name: "Two zones",
          },
        ]}
      />
      <Typography level="title-md">Base scoring</Typography>
      <Box sx={{ paddingBottom: 2 }}>
        <Stack direction="row" spacing={1} alignItems={"center"}>
          <CheckboxInput name="customSettings.useFailedAttemptPenalty" label="Use fixed penalty" />
          <NumberInput
            name="customSettings.failedAttemptPenalty"
            label={null}
            decimalScale={2}
            disabled={!enableFixedPenalty}
            sx={enableFixedPenalty ? {} : { opacity: 0 }}
          />
        </Stack>
        <FormHelperText>
          If checked, the penalty for each failed attempt is always the specified value
        </FormHelperText>
        {enableFixedPenalty ? (
          <Box sx={{ paddingTop: 1 }}>
            <NumberInput name="customSettings.flashBonus" label="Flash bonus" decimalScale={2} />
            <FormHelperText>
              Extra points for topping a route first try. Only applies when a fixed penalty is used
            </FormHelperText>
          </Box>
        ) : null}
      </Box>

      <Alert startDecorator={<Info fontSize="small" />} sx={{ alignItems: "flex-start" }}>
        <Typography level="body-sm">
          The table below is the base scoring configuration. It can be customised on a per-route
          level{" "}
          <Link from="/gym/$venueUuid/events/$competitionUuid/profile/scoring" to="../../routes">
            on the Routes tab
          </Link>{" "}
          after you have saved changes here.
        </Typography>
      </Alert>
      <CustomScoringTableInput
        name="customSettings.baseRoutePointsConfig"
        segmentsCount={segmentsCount}
        hasFixedPenalty={enableFixedPenalty}
      />
      <Typography level="title-md">Contributing routes</Typography>
      <Stack spacing={1} marginBottom={1}>
        <Typography level="body-sm">
          How many ticked routes contribute towards the overall score?
        </Typography>
        <ToggleButtonSelect
          options={[
            {
              value: "ALL",
              name: "All routes",
            },
            {
              value: "TOP_N",
              name: "Top-N routes",
            },
          ]}
          value={isAll ? "ALL" : "TOP_N"}
          onChange={(v) => setIsAll(v === "ALL")}
        />
      </Stack>
      {!isAll ? <NumberInput name="customSettings.maxRoutes" label="Top-N routes" /> : null}
      <Box sx={{ paddingTop: 2 }}>
        <TextareaInput
          name="customSettings.rulesText"
          label={<Typography level="title-md">Rules text</Typography>}
          minRows={3}
          maxRows={5}
          placeholder="e.g. 1 point per zone, 2 points per top"
          helperText="This text will be visible to competitors in the rules section. Use it to explain how points are awarded."
        />
      </Box>
    </>
  );
};

const AttemptsFormFields = () => {
  const useZones = useWatch({ name: "attemptsSettings.useZones" });
  const { setValue } = useFormContext();

  useEffect(() => {
    if (!useZones) {
      setValue("attemptsSettings.segment", null);
      setValue("attemptsSettings.segment2", null);
    }
  }, [useZones, setValue]);

  return (
    <>
      <Box sx={{ paddingY: 1 }}>
        <Divider sx={{ marginX: -1.5 }} />
      </Box>
      <Typography level="title-lg">System options</Typography>
      <NumberInput name="attemptsSettings.flash" label="Flash points" decimalScale={2} />
      <NumberInput name="attemptsSettings.secondTry" label="Second try points" decimalScale={2} />
      <NumberInput name="attemptsSettings.thirdTry" label="Third try points" />
      <NumberInput
        name="attemptsSettings.fourPlusTries"
        label="4 or more tries points"
        decimalScale={2}
      />
      <CheckboxInput name="attemptsSettings.useZones" label="Use zone holds?" />
      {useZones ? (
        <>
          <NumberInput
            name="attemptsSettings.segment"
            label="Zone bonus (optional)"
            decimalScale={2}
          />
          <NumberInput
            name="attemptsSettings.segment2"
            label="Zone 2 bonus (optional)"
            decimalScale={2}
          />
        </>
      ) : null}
    </>
  );
};

const TicksOnlyFormFields = () => {
  const useZones = useWatch({ name: "ticksOnlySettings.useZones" });
  const { setValue } = useFormContext();

  useEffect(() => {
    if (!useZones) {
      setValue("ticksOnlySettings.segment", null);
      setValue("ticksOnlySettings.segment2", null);
    }
  }, [useZones, setValue]);
  return (
    <>
      <Box sx={{ paddingY: 2 }}>
        <Divider sx={{ marginX: -1.5 }} />
      </Box>
      <Typography level="title-lg">System options</Typography>
      <NumberInput name="ticksOnlySettings.tick" label="Tick points" decimalScale={2} />
      <NumberInput name="ticksOnlySettings.flashBonus" label="Flash bonus" decimalScale={2} />
      <CheckboxInput name="ticksOnlySettings.useZones" label="Use zone holds?" />
      {useZones ? (
        <>
          <NumberInput
            name="ticksOnlySettings.segment"
            label="Zone points (optional)"
            decimalScale={2}
          />
          <NumberInput
            name="ticksOnlySettings.segment2"
            label="Zone 2 points (optional)"
            decimalScale={2}
          />
        </>
      ) : null}
    </>
  );
};

const DynamicFormFields = () => {
  return (
    <>
      <Box sx={{ paddingY: 1 }}>
        <Divider sx={{ marginX: -1.5 }} />
      </Box>
      <Typography level="title-lg">System options</Typography>
      <NumberInput name="dynamicSettings.basePoints" label="Points per problem" decimalScale={2} />
      <NumberInput name="dynamicSettings.flashBonus" label="Flash bonus" decimalScale={2} />
      <CheckboxInput
        name="dynamicSettings.sharePointsWithinCategory"
        label="Share points within category"
      />
      <FormHelperText>
        If checked, points for each problem are shared between all climbers in each category, rather
        than everyone.
      </FormHelperText>
    </>
  );
};

const CircuitNumberFormFields = () => {
  const maxRoutes = useWatch({ name: "circuitNumberSettings.maxRoutes" });
  const { setValue } = useFormContext();
  const [isAll, setIsAll] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (isAll) {
      setValue("circuitNumberSettings.maxRoutes", null);
    }
  }, [isAll, setValue]);

  useEffect(() => {
    if (isAll === undefined) {
      setIsAll(!maxRoutes);
    }
  }, [isAll, maxRoutes]);

  return (
    <>
      <Box sx={{ paddingY: 1 }}>
        <Divider sx={{ marginX: -1.5 }} />
      </Box>
      <Typography level="title-lg">System options</Typography>
      <NumberInput
        name="circuitNumberSettings.pointsMultiplier"
        label="Points multiplier"
        decimalScale={2}
      />
      <Stack spacing={1} marginBottom={1}>
        <Typography level="body-sm">
          How many ticked routes contribute towards the overall score?
        </Typography>
        <ToggleButtonSelect
          options={[
            {
              value: "ALL",
              name: "All routes",
            },
            {
              value: "TOP_N",
              name: "Top-N routes",
            },
          ]}
          value={isAll ? "ALL" : "TOP_N"}
          onChange={(v) => setIsAll(v === "ALL")}
        />
      </Stack>
      {!isAll ? <NumberInput name="circuitNumberSettings.maxRoutes" label="Top-N routes" /> : null}
      <NumberInput name="circuitNumberSettings.flashBonus" label="Flash bonus" decimalScale={2} />
    </>
  );
};

const IFSCRankingFormFields = () => {
  return null;
};

const IFSCSportFormFields = () => {
  return null;
};

export default CompetitionScoringForm;
